import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Eye as Visibility,
  CheckCircle2 as CheckCircle,
  Pencil as Edit,
  X as Cancel,
  Star as RateReview,
  RefreshCw as Refresh,
} from "lucide-react";
import reviewService from "../services/reviewService";
import { useAuth } from "../context/AuthContext";
import { logger } from "../services/logService";
import toast from "react-hot-toast";
import TestCaseFormModal from "../components/TestCaseFormModal";
import { storeClientId } from "../utils/tokenUtils";
import TableSkeleton from "../components/skeletons/TableSkeleton";

const ReviewTestCasesPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("project_id");
  const clientId = searchParams.get("client_id");

  // Store client_id from URL if provided
  useEffect(() => {
    if (clientId) {
      storeClientId(clientId);
    }
  }, [clientId]);

  const [reviewerTestCases, setReviewerTestCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10);
  const [reviewStatusFilter, setReviewStatusFilter] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Modal states
  const [showTestCaseModal, setShowTestCaseModal] = useState(false);
  const [editingTestCase, setEditingTestCase] = useState(null);
  const [isViewMode, setIsViewMode] = useState(false);

  // Load reviewer test cases
  useEffect(() => {
    const loadReviewerTestCases = async () => {
      if (!user?.user_id) {
        return;
      }

      try {
        setLoading(true);
        const response = await reviewService.getMyTestCases({
          page,
          limit,
          review_status: reviewStatusFilter,
        });

        if (response.success && response.data) {
          const testCases = response.data.test_cases || [];
          setReviewerTestCases(testCases);
          setTotalPages(response.data.totalPages || 1);
          setTotal(response.data.total || 0);
        } else {
          console.warn("[Reviewer] Response was not successful:", response);
          setReviewerTestCases([]);
        }
      } catch (error) {
        console.error("[Reviewer] Error loading reviewer test cases:", error);
        logger.error(error, "Failed to load reviewer test cases");
        toast.error("Failed to load test cases for review");
        setReviewerTestCases([]);
      } finally {
        setLoading(false);
      }
    };

    loadReviewerTestCases();
  }, [user?.user_id, page, reviewStatusFilter, refreshTrigger, limit]);

  const handleViewTestCase = (testCase) => {
    setEditingTestCase(testCase);
    setIsViewMode(true);
    setShowTestCaseModal(true);
  };

  const handleCloseModal = () => {
    setShowTestCaseModal(false);
    setEditingTestCase(null);
    setIsViewMode(false);
  };

  const handleApprove = async (testCase) => {
    if (!testCase.review_id) {
      toast.error("Review ID not found");
      return;
    }

    try {
      await reviewService.approveTestCase(testCase.review_id);
      toast.success("Test case approved successfully");
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      logger.error(error, "Failed to approve test case");
      toast.error("Failed to approve test case");
    }
  };

  const handleRequestChanges = async (testCase) => {
    if (!testCase.review_id) {
      toast.error("Review ID not found");
      return;
    }

    const comments = prompt("Please provide comments for requested changes:");
    if (!comments || !comments.trim()) {
      return;
    }

    try {
      await reviewService.requestChanges(testCase.review_id, comments);
      toast.success("Changes requested successfully");
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      logger.error(error, "Failed to request changes");
      toast.error("Failed to request changes");
    }
  };

  const handleReject = async (testCase) => {
    if (!testCase.review_id) {
      toast.error("Review ID not found");
      return;
    }

    const comments = prompt("Please provide reason for rejection:");
    if (!comments || !comments.trim()) {
      return;
    }

    try {
      await reviewService.rejectTestCase(testCase.review_id, comments);
      toast.success("Test case rejected successfully");
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      logger.error(error, "Failed to reject test case");
      toast.error("Failed to reject test case");
    }
  };

  const columns = [
    {
      field: "title",
      key: "title",
      label: "Title",
      render: (value, row) => (
        <span className="font-medium text-text-primary">
          {value || "Untitled"}
        </span>
      ),
    },
    {
      field: "project_name",
      key: "project_name",
      label: "Project",
      render: (value) => (
        <span className="text-text-secondary">{value || "N/A"}</span>
      ),
    },
    {
      field: "status",
      key: "status",
      label: "Status",
      render: (value) => {
        const statusConfig = {
          approved: "bg-green-100 text-green-800",
          active: "bg-green-100 text-green-800",
          review: "bg-yellow-100 text-yellow-800",
          requested_changes: "bg-orange-100 text-orange-800",
          rejected: "bg-red-100 text-red-800",
          deprecated: "bg-bg-tertiary text-text-secondary",
          draft: "bg-bg-tertiary text-text-secondary",
        };
        const statusClass =
          statusConfig[value] || "bg-bg-tertiary text-text-secondary";
        const displayText =
          value === "requested_changes"
            ? "Requested Changes"
            : value || "draft";
        return (
          <span className={`px-2 py-1 text-xs rounded-full ${statusClass}`}>
            {displayText}
          </span>
        );
      },
    },
    {
      field: "review_status_detail",
      key: "review_status_detail",
      label: "Review Status",
      render: (value) => (
        <span
          className={`px-2 py-1 text-xs rounded-full ${
            value === "pending"
              ? "bg-yellow-100 text-yellow-800"
              : value === "approved"
                ? "bg-green-100 text-green-800"
                : value === "request_changes"
                  ? "bg-orange-100 text-orange-800"
                  : value === "rejected"
                    ? "bg-red-100 text-red-800"
                    : "bg-bg-tertiary text-text-secondary"
          }`}
        >
          {value || "pending"}
        </span>
      ),
    },
    {
      field: "created_by_name",
      key: "created_by_name",
      label: "Created By",
      render: (value) => (
        <span className="text-text-secondary">{value || "N/A"}</span>
      ),
    },
    {
      field: "actions",
      key: "actions",
      label: "Actions",
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleViewTestCase(row);
            }}
            className="text-indigo-600 hover:text-indigo-800 font-medium text-sm"
            title="View Test Case"
          >
            <Visibility className="w-4 h-4" />
          </button>
          {row.review_status_detail === "pending" && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleApprove(row);
                }}
                className="text-green-600 hover:text-green-800 font-medium text-sm"
                title="Approve"
              >
                <CheckCircle className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRequestChanges(row);
                }}
                className="text-orange-600 hover:text-orange-800 font-medium text-sm"
                title="Request Changes"
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleReject(row);
                }}
                className="text-red-600 hover:text-red-800 font-medium text-sm"
                title="Reject"
              >
                <Cancel className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-bg-secondary">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-bg-primary rounded-lg shadow mb-6 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <RateReview className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-text-primary">
                  Review Test Cases
                </h1>
                <p className="text-sm text-text-secondary mt-1">
                  Test cases assigned to you for review
                </p>
              </div>
              {total > 0 && (
                <span className="px-3 py-1 bg-blue-600 text-white rounded-full text-sm font-medium">
                  {total} {total === 1 ? "test case" : "test cases"}
                </span>
              )}
            </div>
            <button
              onClick={() => setRefreshTrigger((prev) => prev + 1)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2"
            >
              <Refresh className="w-4 h-4" />
              Refresh
            </button>
          </div>

          {/* Filters */}
          <div className="mt-4 flex items-center gap-4">
            <label className="text-sm font-medium text-text-primary">
              Filter by Status:
            </label>
            <select
              value={reviewStatusFilter || ""}
              onChange={(e) => {
                setReviewStatusFilter(e.target.value || null);
                setPage(1);
              }}
              className="px-3 py-2 bg-bg-primary text-text-primary border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="request_changes">Request Changes</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        {/* Test Cases Table */}
        <div className="bg-bg-primary rounded-lg shadow">
          {loading ? (
            <div className="p-4">
              <TableSkeleton rows={8} columns={6} />
            </div>
          ) : reviewerTestCases.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border-primary">
                  <thead className="bg-bg-secondary">
                    <tr>
                      {columns.map((col) => (
                        <th
                          key={col.key}
                          className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-bg-primary divide-y divide-border-primary">
                    {reviewerTestCases.map((testCase) => (
                      <tr
                        key={testCase.test_case_id}
                        className="hover:bg-bg-hover cursor-pointer"
                        onClick={() => handleViewTestCase(testCase)}
                      >
                        {columns.map((col) => {
                          const fieldName = col.field || col.key;
                          const cellValue = testCase[fieldName];
                          return (
                            <td
                              key={col.key}
                              className="px-6 py-4 whitespace-nowrap text-sm text-text-primary"
                            >
                              {col.render
                                ? col.render(cellValue, testCase)
                                : cellValue}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-border-primary flex items-center justify-between">
                  <div className="text-sm text-text-primary">
                    Showing {(page - 1) * limit + 1} to{" "}
                    {Math.min(page * limit, total)} of {total} test cases
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                      disabled={page === 1}
                      className="px-4 py-2 bg-bg-primary border border-border-primary rounded-md text-sm font-medium text-text-primary hover:bg-bg-hover disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="px-4 py-2 text-sm text-text-primary">
                      Page {page} of {totalPages}
                    </span>
                    <button
                      onClick={() =>
                        setPage((prev) => Math.min(totalPages, prev + 1))
                      }
                      disabled={page === totalPages}
                      className="px-4 py-2 bg-bg-primary border border-border-primary rounded-md text-sm font-medium text-text-primary hover:bg-bg-hover disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <RateReview className="w-16 h-16 text-text-tertiary mx-auto mb-4" />
              <p className="text-text-secondary text-lg">
                No test cases assigned for review
              </p>
              <p className="text-text-tertiary text-sm mt-2">
                Test cases assigned to you will appear here
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Test Case View Modal */}
      <TestCaseFormModal
        isOpen={showTestCaseModal}
        onClose={handleCloseModal}
        onSubmit={() => {}} // Read-only view
        initialData={editingTestCase}
        projectId={projectId || editingTestCase?.project_id}
        viewMode={isViewMode}
        onEdit={() => {
          setIsViewMode(false);
        }}
      />
    </div>
  );
};

export default ReviewTestCasesPage;
